import React, { useState, useEffect } from "react";
import styles from "./minesweeper.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards, getUserDetails1 } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import task from "../Task.js";
import { useAudio } from "../../../Providers/AudioProvider";

const Minesweeper = () => {
  const [board, setBoard] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [flags, setFlags] = useState([]);
  const [mines, setMines] = useState([]);
  const [firstClick, setFirstClick] = useState(true);
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [score, setScore] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem("minesweeperHighScore");
    return saved ? parseInt(saved) : 0;
  });
  const [apiHighScore, setApiHighScore] = useState(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastTapCell, setLastTapCell] = useState(null);
  const DOUBLE_TAP_DELAY = 300; // 300ms for double tap
  const playClick = useAudio();

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;
  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };
  const BOARD_SIZE = 9;
  const MINE_COUNT = 10;
  const POINTS_PER_CELL = 5;
  const BONUS_POINTS = 5;
  const TIME_BONUS_MULTIPLIER = 5;

  // Initialize the board
  const initializeBoard = () => {
    const newBoard = Array(BOARD_SIZE)
      .fill()
      .map(() =>
        Array(BOARD_SIZE)
          .fill()
          .map(() => ({
            revealed: false,
            isMine: false,
            neighborMines: 0,
          }))
      );

    // Place mines randomly for preview
    const newMines = [];
    let minesPlaced = 0;

    while (minesPlaced < MINE_COUNT) {
      const x = Math.floor(Math.random() * BOARD_SIZE);
      const y = Math.floor(Math.random() * BOARD_SIZE);

      if (newBoard[x][y].isMine) continue;

      newBoard[x][y].isMine = true;
      newMines.push({ x, y });
      minesPlaced++;
    }

    // Calculate neighbor mines
    for (let x = 0; x < BOARD_SIZE; x++) {
      for (let y = 0; y < BOARD_SIZE; y++) {
        if (!newBoard[x][y].isMine) {
          let count = 0;
          for (let dx = -1; dx <= 1; dx++) {
            for (let dy = -1; dy <= 1; dy++) {
              const nx = x + dx;
              const ny = y + dy;
              if (
                nx >= 0 &&
                nx < BOARD_SIZE &&
                ny >= 0 &&
                ny < BOARD_SIZE &&
                newBoard[nx][ny].isMine
              ) {
                count++;
              }
            }
          }
          newBoard[x][y].neighborMines = count;
        }
      }
    }

    setBoard(newBoard);
    setFlags([]);
    setMines(newMines);
    setGameOver(false);
    setGameWon(false);
    setFirstClick(true);
    setTimer(0);
    setIsRunning(false);
    setScore(0);
    setShowPreview(true);

    // Hide preview after 3 seconds
    setTimeout(() => {
      setShowPreview(false);
    }, 2500);
  };

  // Calculate score for a single cell
  const calculateCellScore = () => {
    return POINTS_PER_CELL; // Just return 10 points per cell, no time bonus
  };

  // Update score when a cell is revealed
  const updateScore = () => {
    const cellScore = calculateCellScore();
    setScore((prevScore) => prevScore + cellScore);
  };

  // Update high score
  const updateHighScore = (newScore) => {
    const currentHighScore = Math.max(highScore, apiHighScore);
    if (newScore > currentHighScore) {
      setHighScore(newScore);
      localStorage.setItem("minesweeperHighScore", newScore.toString());
      setIsNewHighScore(true);
      setTimeout(() => setIsNewHighScore(false), 2000); // Reset after 2 seconds
    }
  };

  // Handle cell click
  const handleCellClick = (x, y) => {
    if (gameOver || gameWon || flags.some((f) => f.x === x && f.y === y))
      return;

    if (firstClick) {
      setFirstClick(false);
      setIsRunning(true);
      return;
    }

    const newBoard = [...board];
    if (newBoard[x][y].isMine) {
      revealAllMines();
      handleGameOver();
      return;
    }

    revealCell(x, y);
    checkWinCondition();
  };

  // Reveal cell and its neighbors if empty
  const revealCell = (x, y) => {
    if (
      x < 0 ||
      x >= BOARD_SIZE ||
      y < 0 ||
      y >= BOARD_SIZE ||
      board[x][y].revealed ||
      board[x][y].isMine // Don't reveal or score mines
    )
      return;

    const newBoard = [...board];
    newBoard[x][y].revealed = true;

    // Update score for each revealed cell
    updateScore();

    if (newBoard[x][y].neighborMines === 0) {
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          revealCell(x + dx, y + dy);
        }
      }
    }

    setBoard(newBoard);
  };

  // Handle touch start
  const handleTouchStart = (e, x, y) => {
    if (gameOver || gameWon || board[x][y].revealed) return;

    const currentTime = Date.now();
    const tapLength = currentTime - lastTapTime;

    if (
      lastTapCell &&
      lastTapCell.x === x &&
      lastTapCell.y === y &&
      tapLength < DOUBLE_TAP_DELAY
    ) {
      // Double tap detected - place/remove flag
      handleFlagPlacement(x, y);
      setLastTapTime(0);
      setLastTapCell(null);
    } else {
      // First tap
      setLastTapTime(currentTime);
      setLastTapCell({ x, y });
    }
  };

  // Handle touch end
  const handleTouchEnd = (e, x, y) => {
    if (!lastTapCell) return;

    const currentTime = Date.now();
    const tapLength = currentTime - lastTapTime;

    if (tapLength >= DOUBLE_TAP_DELAY) {
      // Single tap - reveal cell
      handleCellClick(x, y);
      setLastTapTime(0);
      setLastTapCell(null);
    }
  };

  // Handle touch cancel
  const handleTouchCancel = () => {
    setLastTapTime(0);
    setLastTapCell(null);
  };

  // Handle flag placement
  const handleFlagPlacement = (x, y) => {
    if (gameOver || gameWon || board[x][y].revealed) return;

    const newFlags = [...flags];
    const flagIndex = newFlags.findIndex((f) => f.x === x && f.y === y);

    if (flagIndex === -1) {
      // Check if we've reached the maximum number of flags
      if (newFlags.length >= MINE_COUNT) {
        return; // Don't add more flags if we've reached the limit
      }
      newFlags.push({ x, y });
    } else {
      newFlags.splice(flagIndex, 1);
    }

    setFlags(newFlags);
  };

  // Reveal all mines when game is over
  const revealAllMines = () => {
    const newBoard = [...board];
    mines.forEach(({ x, y }) => {
      newBoard[x][y].revealed = true;
    });
    setBoard(newBoard);
  };

  // Check if player has won
  const checkWinCondition = () => {
    let unrevealedCount = 0;
    let revealedCells = 0;
    for (let x = 0; x < BOARD_SIZE; x++) {
      for (let y = 0; y < BOARD_SIZE; y++) {
        if (!board[x][y].revealed) unrevealedCount++;
        if (board[x][y].revealed && !board[x][y].isMine) revealedCells++;
      }
    }
    if (unrevealedCount === MINE_COUNT) {
      // Add win bonus and time bonus (capped at 50)
      const timeBonus = Math.min(50, Math.max(0, 50 - timer));
      const totalBonus = BONUS_POINTS + timeBonus;
      if (!gameOver) {
        const finalScore = score + totalBonus;
        setScore(finalScore);
        updateHighScore(finalScore);
        setIsRunning(false);
        // Only call API if there are points to submit
        if (finalScore > 0) {
          handleApiResponse(finalScore, true);
        } else {
          setGameWon(true);
        }
      }
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  // Fetch initial high score from API on component mount
  useEffect(() => {
    const fetchInitialHighScore = async () => {
      try {
        const res = await getUserDetails1();
        if (res && res.gameRewards && res.gameRewards.games) {
          const minesweeperGame = res.gameRewards.games.find(
            (game) => game.gameType === "minesweeper"
          );
          if (minesweeperGame && minesweeperGame.highScore) {
            setApiHighScore(minesweeperGame.highScore);
            // Update local high score if API high score is higher
            if (minesweeperGame.highScore > highScore) {
              setHighScore(minesweeperGame.highScore);
              localStorage.setItem(
                "minesweeperHighScore",
                minesweeperGame.highScore.toString()
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial high score:", error);
      }
    };

    fetchInitialHighScore();
  }, []);

  // Initialize board on component mount
  useEffect(() => {
    initializeBoard();
  }, []);

  // Add analytics logging
  useEffect(() => {
    logPageView("minesweeper", "minesweeper");
  }, []);

  const handleApiResponse = async (points, isWin = false) => {
    // Only make API call if there are points to submit
    if (points <= 0) {
      if (isWin) {
        setGameWon(true);
      } else {
        setGameOver(true);
      }
      return;
    }

    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "minesweeper",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));

        // Extract high score from API response
        const minesweeperGame = res?.gameRewards?.games?.find(
          (game) => game.gameType === "minesweeper"
        );
        if (minesweeperGame && minesweeperGame.highScore) {
          setApiHighScore(minesweeperGame.highScore);
          // Update local high score if API high score is higher
          if (minesweeperGame.highScore > highScore) {
            setHighScore(minesweeperGame.highScore);
            localStorage.setItem(
              "minesweeperHighScore",
              minesweeperGame.highScore.toString()
            );
          }
        }

        // Show appropriate popup after API call
        if (isWin) {
          setGameWon(true);
        } else {
          setGameOver(true);
        }
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
      // Show popup even if API call fails
      if (isWin) {
        setGameWon(true);
      } else {
        setGameOver(true);
      }
    }
  };

  // Handle game over
  const handleGameOver = () => {
    setIsRunning(false);
    updateHighScore(score);
    // Only call API if there are points to submit
    if (score > 0) {
      handleApiResponse(score, false);
    } else {
      setGameOver(true);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.welcome_text}>MINE SWEEPER</h3>
      <div className={styles.infoGrid}>
        <div className={styles.infoCol}>
          <div className={styles.infoLabel}>Time</div>
          <div className={styles.infoValue}>{timer}</div>
        </div>
        <div className={styles.infoCol}>
          <div className={styles.infoLabel}>Flags</div>
          <div className={styles.infoValue}>{MINE_COUNT - flags.length}</div>
        </div>
        <div className={styles.infoCol}>
          <div className={styles.infoLabel}>Score</div>
          <div className={styles.infoValue}>
            {score}
            <span className={styles.scoreLabel}>(+{POINTS_PER_CELL})</span>
          </div>
        </div>
        <div className={styles.infoCol}>
          <div className={styles.infoLabel}>High Score</div>
          <div className={styles.infoValue}>
            <span
              className={`${styles.trophyIcon} ${
                isNewHighScore ? styles.pulse : ""
              }`}
            >
              🏆
            </span>
            <span className={isNewHighScore ? styles.pulse : ""}>
              {Math.max(highScore, apiHighScore)}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.board}>
        {board.map((row, x) => (
          <div key={x} className={styles.row}>
            {row.map((cell, y) => (
              <div
                key={`${x}-${y}`}
                className={`${styles.cell} ${
                  cell.revealed ? styles.revealed : ""
                } ${
                  cell.isMine && (cell.revealed || showPreview)
                    ? styles.mine
                    : ""
                }`}
                onClick={() => handleCellClick(x, y)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  handleFlagPlacement(x, y);
                }}
                onTouchStart={(e) => handleTouchStart(e, x, y)}
                onTouchEnd={(e) => handleTouchEnd(e, x, y)}
                onTouchCancel={handleTouchCancel}
                data-number={
                  cell.revealed && !cell.isMine ? cell.neighborMines : undefined
                }
              >
                {cell.revealed || showPreview
                  ? cell.isMine
                    ? "💣"
                    : cell.neighborMines > 0 && cell.neighborMines
                  : flags.some((f) => f.x === x && f.y === y)
                  ? "🚩"
                  : ""}
              </div>
            ))}
          </div>
        ))}
      </div>

      {showPreview && (
        <div className={styles.previewMessage}>
          Preview: Mines will be hidden in 3 seconds...
        </div>
      )}

      <div className="d-flex justify-content-center mt-3">
        <button
          className="btn btn-success d-flex align-items-center gap-2"
          onClick={initializeBoard}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
          </svg>
          RESET GAME
        </button>
      </div>
      <img
        onClick={() => {
          toogleMenu(task, "task");
        }}
        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
        className={styles.cancel_full}
        style={{ cursor: "pointer" }}
      />
      <p
        className={styles.quest}
        onClick={() => setShowInstructions(true)}
        title="How to Play"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
        </svg>
      </p>

      {gameOver && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className={styles.quento_epic}>Game Over!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={() => {
                  setGameOver(false);
                  initializeBoard();
                }}
              />
              <div className={styles.quento_epic_div}>
                <p className={styles.quento_pointsLabel}>SCORE</p>
                <div className={styles.quento_pointsValue1}>
                  <img
                    src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                    alt="coin"
                  />
                  <p className={styles.quento_pointsNumber}>{score}</p>
                </div>
              </div>
              <p className={styles.quento_bottom_text}>
                Keep playing! Challenge yourself to beat your high score!
              </p>
              <button
                className={styles.control_btn}
                onClick={() => {
                  setGameOver(false);
                  initializeBoard();
                }}
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {gameWon && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className={styles.quento_epic}>Congratulations!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={() => {
                  setGameWon(false);
                  initializeBoard();
                }}
              />
              {!IsPointsReached ? (
                <div className={styles.quento_epic_div}>
                  <p className={styles.quento_pointsLabel}>TOKENS EARNED</p>
                  <div className={styles.quento_pointsValue1}>
                    <img
                      src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                      alt="coin"
                    />
                    <p className={styles.quento_pointsNumber}>{score}</p>
                  </div>
                </div>
              ) : (
                <div className={styles.quento_epic_div}>
                  <p className={styles.quento_pointsLabel}>
                    POINTS EARNED: {score} <br />
                    TOKENS EARNED: NIL
                  </p>
                </div>
              )}
              <p className={styles.quento_bottom_text}>
                Amazing job! You've cleared all the mines!
              </p>
              <button
                className={styles.control_btn}
                onClick={() => {
                  setGameWon(false);
                  initializeBoard();
                }}
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showInstructions && (
        <div className={styles.overlay}>
          <div className={styles.quento_full_popup}>
            <div className={styles.quento_popup_content}>
              <h2 className={styles.quento_epic}>How to Play</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className={styles.quento_cancel_img}
                onClick={() => setShowInstructions(false)}
              />
              <div className={styles.quento_epic_div}>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>👆</div>
                  <p className={styles.instruction_text}>
                    Tap to reveal a cell
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>👆</div>
                  <p className={styles.instruction_text}>
                    Long press to place/remove a flag
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>🚩</div>
                  <p className={styles.instruction_text}>
                    Use flags to mark potential mine locations
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>💣</div>
                  <p className={styles.instruction_text}>
                    Avoid clicking on mines!
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>🎯</div>
                  <p className={styles.instruction_text}>
                    Numbers show how many mines are adjacent to that cell
                  </p>
                </div>
                <div className={styles.instruction_item}>
                  <div className={styles.instruction_icon}>🏆</div>
                  <p className={styles.instruction_text}>
                    Win by revealing all non-mine cells
                  </p>
                </div>
              </div>
              <button
                className={styles.control_btn}
                onClick={() => setShowInstructions(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Minesweeper;
